import type { Color } from "@mui/material";
import { amber, blueGrey, green, red } from "@mui/material/colors";
import type { Inventory, InventoryLevel, InventoryStatus } from "@/api/inventory/_models/Inventory";

/** A collapsed inventory row's height in px, bottom border included (shared by the row and its skeleton). */
export const INVENTORY_LIST_ROW_HEIGHT_PX = 49;
/** A collapsed row's content height in px, without the 1px bottom border. */
export const INVENTORY_LIST_ROW_CONTENT_PX = INVENTORY_LIST_ROW_HEIGHT_PX - 1;

/** Each inventory status's display label. */
export const INVENTORY_LIST_STATUS_LABEL: Record<InventoryStatus, string> = {
  match: "Match",
  differs: "Differs",
  missingInCin7: "Missing in Cin7",
  missingAt3pl: "Missing at 3PL",
  cin7Only: "Cin7 only",
  threePlOnly: "3PL only",
};

/** Each inventory status's chip hue. */
export const INVENTORY_LIST_STATUS_HUE: Record<InventoryStatus, Color> = {
  match: green,
  differs: amber,
  missingInCin7: red,
  missingAt3pl: red,
  cin7Only: blueGrey,
  threePlOnly: blueGrey,
};

/** Each source system's display name. */
export const INVENTORY_LIST_SYSTEM_LABEL: Record<string, string> = {
  CIN7: "Cin7",
  ESTORE: "eStore",
  TOLL: "Toll",
};

/** A row's Cin7 level, if Cin7 reports on it. */
export const toCin7Level = (row: Inventory): InventoryLevel | undefined => {
  return row.levels.find((level) => level.system === "CIN7");
};

/** A row's 3PL levels (eStore, Toll) — usually one. */
export const toThreePlLevelList = (row: Inventory): InventoryLevel[] => {
  return row.levels.filter((level) => level.system !== "CIN7");
};

/** The 3PLs' on-hand less Cin7's (missing counts as 0), or null when no 3PL reports on the row. */
export const toOnHandDifference = (row: Inventory): number | null => {
  const threePl = toThreePlLevelList(row);
  if (threePl.length === 0) return null;
  const cin7OnHand = toCin7Level(row)?.onHand ?? 0;
  const threePlOnHand = threePl.reduce((sum, level) => sum + (level.onHand ?? 0), 0);
  return threePlOnHand - cin7OnHand;
};
